import { Tab as HeadlessTab } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';

interface TabItem {
  label: string | React.ReactNode;
  content: React.ReactNode;
}

interface TabShared {
  items: TabItem[];
  onChange?: (index: number) => void;
  className?: string;
  panelWrapperClassName?: string;
  panelClassName?: string;
}

type ControlledProps = TabShared & {
  selectedIndex: number;
  defaultIndex?: never; // disallow both at once (optional)
};

type UncontrolledProps = TabShared & {
  selectedIndex?: never;
  defaultIndex?: number;
};

export type TabProps = ControlledProps | UncontrolledProps;

export const Tab = (props: TabProps) => {
  const { items, onChange, className, panelWrapperClassName, panelClassName } =
    props;

  const isControlled = 'selectedIndex' in props;

  return (
    <HeadlessTab.Group
      {...(isControlled
        ? { selectedIndex: props.selectedIndex }
        : { defaultIndex: props.defaultIndex ?? 0 })}
      onChange={onChange}
    >
      <HeadlessTab.List
        className={clsx('flex gap-2 border-b border-neutral-100', className)}
      >
        {items.map((item) => (
          <HeadlessTab as={Fragment} key={`tab-panel-${items.indexOf(item)}`}>
            {({ selected }) => (
              <button
                className={clsx(
                  'py-2 font-medium transition-all duration-200 focus:outline-none border-b-2 mx-4',
                  selected
                    ? 'border-primary text-primary'
                    : 'border-transparent text-neutral-400 hover:text-neutral-600',
                )}
              >
                {item.label}
              </button>
            )}
          </HeadlessTab>
        ))}
      </HeadlessTab.List>
      <HeadlessTab.Panels
        className={clsx(panelWrapperClassName ? panelWrapperClassName : 'mt-4')}
      >
        {items.map((item) => (
          <HeadlessTab.Panel
            key={`tab-panel-${items.indexOf(item)}`}
            className={clsx(
              panelClassName ? panelClassName : 'focus:outline-none',
            )}
          >
            {item.content}
          </HeadlessTab.Panel>
        ))}
      </HeadlessTab.Panels>
    </HeadlessTab.Group>
  );
};
